import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Loader2, MessageSquareText, Send, Zap } from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import useMotivosDeEncerramento from "../../hooks/useMotivosDeEncerramento";
import EscolherMotivoDeEncerramento from "../EscolherMotivoDeEncerramento";

// Modal do botão "Resolver".
//
// Encerrar era um clique só e sem volta. Aqui a pessoa confirma, diz por que
// está encerrando e decide se pergunta ao cliente antes — quem já ouviu
// "obrigado, resolvido" não precisa perguntar de novo.
const ResolverTicketModal = ({ open, onClose, ticket, onResolved }) => {
	const { cadastrados: MOTIVOS } = useMotivosDeEncerramento();
	// Sem escolha ainda, vale o primeiro da lista do administrador. Derivado em
	// vez de gravado no estado porque a lista chega da API depois do primeiro
	// render — um efeito para "escolher o primeiro" corria com a resposta.
	const [motivo, setMotivo] = useState(null);
	const motivoEscolhido = motivo ?? MOTIVOS[0]?.chave ?? null;
	const [observacao, setObservacao] = useState("");
	const [perguntarAoCliente, setPerguntarAoCliente] = useState(false);
	const [salvando, setSalvando] = useState(false);
	const [conexaoPergunta, setConexaoPergunta] = useState(false);
	// Despedida: se sai, e com que texto. O texto pode ser trocado SÓ para este
	// encerramento — "obrigado pelo contato" depois de uma reclamação soa mal,
	// e ninguém vai a Conexões no meio do atendimento.
	//
	// O que a tela SUGERE é derivado, não gravado: a frase do motivo escolhido
	// e, na falta dela, a da conexão. Guardar a sugestão no estado era o bug do
	// "Resolvido" — a lista de motivos chega depois do primeiro render, a
	// sugestão era calculada antes dela e a frase cadastrada no motivo nunca
	// aparecia; quem encerrava tinha de digitá-la de novo.
	//
	// O estado guarda só o que a PESSOA fez: `null` quer dizer "não mexi".
	const [despedidaEditada, setDespedidaEditada] = useState(null);
	const [enviarEscolhido, setEnviarEscolhido] = useState(null);
	const [despedidaDaConexao, setDespedidaDaConexao] = useState("");
	const [respostas, setRespostas] = useState([]);
	const [buscaResposta, setBuscaResposta] = useState("");
	const [mostrarRespostas, setMostrarRespostas] = useState(false);

	// Grupo não tem "o cliente": qualquer um responderia pelos outros.
	const ehGrupo = Boolean(ticket?.isGroup);

	const sugestao =
		MOTIVOS.find((m) => m.chave === motivoEscolhido)?.despedida ||
		despedidaDaConexao;
	const despedida = despedidaEditada ?? sugestao;
	// Sem texto nenhum não há o que mandar: o padrão acompanha, em vez de
	// prometer uma mensagem que não existe. Conta o texto EFETIVO, não só a
	// sugestão: quem digitou uma frase e trocou para um motivo sem frase
	// cadastrada via o campo inteiro sumir com o que tinha escrito.
	const enviarDespedida = enviarEscolhido ?? Boolean(despedida);

	useEffect(() => {
		if (!open) return;

		setObservacao("");
		// Modal reaberto começa limpo do que a pessoa mexeu da última vez.
		setDespedidaEditada(null);
		setEnviarEscolhido(null);

		// A conexão já pede confirmação? Então o padrão do modal acompanha —
		// quem configurou isso não quer desmarcar a cada encerramento.
		(async () => {
			if (!ticket?.whatsappId) {
				setConexaoPergunta(false);
				setPerguntarAoCliente(false);
				setDespedidaDaConexao("");
				return;
			}

			try {
				const { data } = await api.get(`/whatsapp/${ticket.whatsappId}`);
				// Grupo não tem "o cliente": qualquer um confirmaria pelos outros.
				const liga = Boolean(data?.confirmCloseEnabled) && !ehGrupo;
				setConexaoPergunta(liga);
				setPerguntarAoCliente(liga);
				setDespedidaDaConexao(String(data?.farewellMessage || ""));
			} catch {
				setConexaoPergunta(false);
				setPerguntarAoCliente(false);
				setDespedidaDaConexao("");
			}
		})();
	}, [open, ticket?.whatsappId, ehGrupo]);

	// Trocar o motivo troca a frase sugerida sozinho, porque ela é derivada do
	// motivo. Quem digitou o próprio texto não o perde: `despedidaEditada`
	// continua valendo até a pessoa limpar.
	const escolherMotivo = setMotivo;

	// As respostas rápidas que a equipe já usa na conversa, aqui também: a
	// despedida é uma mensagem como outra qualquer, e reescrever à mão o que
	// já está cadastrado é o tipo de trabalho que o sistema existe para tirar.
	const buscarRespostas = async (termo) => {
		try {
			const { data } = await api.get("/quickAnswers/", {
				params: { searchParam: termo || "" },
			});
			setRespostas(data?.quickAnswers || []);
		} catch {
			setRespostas([]);
		}
	};

	const resolver = async () => {
		setSalvando(true);
		try {
			const { data } = await api.put(`/tickets/${ticket.id}`, {
				status: "closed",
				userId: ticket.userId,
				closeReason: motivoEscolhido || undefined,
				closeNote: observacao.trim() || undefined,
				pedirConfirmacao: perguntarAoCliente,
				enviarDespedida,
				// Só vai quando foi mesmo trocada: mandar o texto igual ao da
				// conexão faria o servidor tratar como personalizada à toa.
				mensagemDeDespedida:
					enviarDespedida && despedida.trim() !== despedidaDaConexao.trim()
						? despedida.trim()
						: undefined,
			});

			// O encerramento deu certo mesmo quando a despedida não saiu (conexão
			// fora do ar, por exemplo) — mas quem pediu a mensagem precisa saber
			// que ela não chegou, senão fica achando que o cliente foi avisado.
			if (data?.despedidaFalhou) {
				toast.warning(
					"Atendimento encerrado, mas a mensagem de encerramento não saiu — confira a conexão.",
				);
			} else {
				toast.success(
					perguntarAoCliente
						? "Pedimos a confirmação ao cliente."
						: "Atendimento encerrado.",
				);
			}

			if (onResolved) onResolved();
			onClose();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	return (
		<Dialog open={open} onOpenChange={(o) => !o && onClose()}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Encerrar atendimento</DialogTitle>
					<DialogDescription>
						{ticket?.contact?.name
							? `Conversa com ${ticket.contact.name}.`
							: "O atendimento sai da sua lista."}
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					<EscolherMotivoDeEncerramento
						motivos={MOTIVOS}
						valor={motivoEscolhido}
						onChange={escolherMotivo}
					/>

					<div className="space-y-1.5">
						<Label htmlFor="closeNote">Observação (opcional)</Label>
						<Textarea
							id="closeNote"
							rows={2}
							value={observacao}
							onChange={(e) => setObservacao(e.target.value)}
							placeholder="Algo que a equipe precise saber depois."
						/>
						<p className="text-xs text-muted-foreground">
							Fica no atendimento, para a equipe. O cliente não vê.
						</p>
					</div>

					<div className="space-y-2 rounded-lg border p-3">
						<div className="flex items-start justify-between gap-4">
							<div className="space-y-0.5">
								<Label
									htmlFor="enviarDespedida"
									className="flex items-center gap-1.5 text-sm"
								>
									<Send className="h-3.5 w-3.5" />
									Enviar mensagem de encerramento
								</Label>
								<p className="text-xs leading-relaxed text-muted-foreground">
									{despedidaDaConexao
										? "Sai para o cliente ao encerrar. Dá para trocar o texto só desta vez."
										: "Esta conexão não tem texto de despedida cadastrado. Escreva um aqui para mandar só desta vez."}
								</p>
							</div>
							<Switch
								id="enviarDespedida"
								checked={enviarDespedida}
								onCheckedChange={setEnviarEscolhido}
							/>
						</div>

						{enviarDespedida && (
							<>
								<Textarea
									id="mensagemDeDespedida"
									rows={2}
									value={despedida}
									onChange={(e) => setDespedidaEditada(e.target.value)}
									placeholder="Obrigado pelo contato! Qualquer coisa, é só chamar."
									aria-label="Texto da mensagem de encerramento"
								/>

								<div className="flex items-center gap-2">
									<Button
										type="button"
										variant="outline"
										size="sm"
										onClick={() => {
											const abrindo = !mostrarRespostas;
											setMostrarRespostas(abrindo);
											if (abrindo) buscarRespostas("");
										}}
									>
										<Zap className="h-3.5 w-3.5" />
										Usar resposta rápida
									</Button>
									{sugestao && despedida.trim() !== sugestao.trim() && (
										<Button
											type="button"
											variant="ghost"
											size="sm"
											onClick={() => setDespedidaEditada(null)}
										>
											Voltar à sugerida
										</Button>
									)}
								</div>

								{mostrarRespostas && (
									<div className="space-y-1.5 rounded-lg border p-2">
										<Input
											value={buscaResposta}
											onChange={(e) => {
												setBuscaResposta(e.target.value);
												buscarRespostas(e.target.value);
											}}
											placeholder="Buscar resposta rápida"
											aria-label="Buscar resposta rápida"
										/>
										{respostas.length === 0 ? (
											<p className="px-1 py-2 text-xs text-muted-foreground">
												Nenhuma resposta rápida encontrada. Elas se cadastram
												em Respostas rápidas, no menu.
											</p>
										) : (
											<ul className="max-h-40 overflow-y-auto">
												{respostas.map((r) => (
													<li key={r.id}>
														<button
															type="button"
															onClick={() => {
																setDespedidaEditada(r.message);
																setMostrarRespostas(false);
															}}
															className="w-full rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
														>
															<span className="block font-medium">
																{r.shortcut}
															</span>
															<span className="block truncate text-xs text-muted-foreground">
																{r.message}
															</span>
														</button>
													</li>
												))}
											</ul>
										)}
									</div>
								)}
								<p className="text-xs text-muted-foreground">
									Trocar aqui vale só para este atendimento — a conexão continua
									como está. Aceita as mesmas variáveis, como
									{" "}
									<code>{"{{name}}"}</code> e <code>{"{{protocolo}}"}</code>.
								</p>
							</>
						)}
					</div>

					{!ehGrupo && (
						<div className="flex items-start justify-between gap-4 rounded-lg border p-3">
							<div className="space-y-0.5">
								<Label
									htmlFor="perguntarAoCliente"
									className="flex items-center gap-1.5 text-sm"
								>
									<MessageSquareText className="h-3.5 w-3.5" />
									Perguntar ao cliente antes
								</Label>
								<p className="text-xs leading-relaxed text-muted-foreground">
									Manda um botão “Finalizar atendimento” e só encerra quando ele
									confirmar. O atendimento continua aberto até lá.
									{!conexaoPergunta && (
										<>
											{" "}
											<span className="text-amber-600 dark:text-amber-500">
												Esta conexão não está configurada para isso — ligue em
												Conexões para usar seus próprios textos.
											</span>
										</>
									)}
								</p>
							</div>
							<Switch
								id="perguntarAoCliente"
								checked={perguntarAoCliente}
								onCheckedChange={setPerguntarAoCliente}
								disabled={!conexaoPergunta}
							/>
						</div>
					)}
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={onClose} disabled={salvando}>
						Cancelar
					</Button>
					<Button onClick={resolver} disabled={salvando}>
						{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
						{perguntarAoCliente ? "Perguntar e aguardar" : "Encerrar agora"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default ResolverTicketModal;
